import React, { useEffect, useState, useRef, useMemo } from 'react';
import Layout from '../../components/Layout';
import InsightModal from '../../components/InsightModal';
import AddThemeModal from '../../components/AddThemeModal';
import EditThemeModal from '../../components/EditThemeModal';
import BoardControlsButton, { BoardColumn, BoardPrefs, loadBoardPrefs, isColumnVisible } from '../../components/BoardControlsButton';
import { insightService, Insight } from '../../services/insightService';
import { themeService, Theme } from '../../services/themeService';
import { organizationService, OrgMember } from '../../services/organizationService';
import { useActiveProject } from '../../hooks/useActiveProject';
import { usePermissions } from '../../hooks/usePermissions';
import { useAuth } from '../../contexts/AuthContext';
import '../../styles/CustomersList.css';
import '../../styles/Pages.css';
import '../../styles/InsightsPage.css';

const INSIGHTS_BOARD_KEY = 'insights:boardControls';
const INSIGHT_COLUMNS: BoardColumn[] = [
  { key: 'description', label: 'Description' },
  { key: 'assignee', label: 'Assignee' },
  { key: 'customer', label: 'Customer' },
  { key: 'sentiment', label: 'Sentiment' },
  { key: 'themes', label: 'Themes' },
];

const ICON_COLORS = ['#6366f1', '#ec4899', '#f59e0b', '#10b981'];
const ICON_BGS    = ['#ede9fe', '#fce7f3', '#fef3c7', '#d1fae5'];

const sentinel = '__editablejs__:';
function getPlainText(details: string | null | undefined): string {
  if (!details) return '';
  let str = details;
  if (str.startsWith(sentinel)) str = str.slice(sentinel.length);
  if (str.trimStart().startsWith('[') || str.trimStart().startsWith('{')) {
    try {
      const nodes = JSON.parse(str);
      const extractText = (node: any): string => {
        if (typeof node.text === 'string') return node.text;
        if (Array.isArray(node.children)) return node.children.map(extractText).join('');
        return '';
      };
      return (Array.isArray(nodes) ? nodes : [nodes])
        .map(extractText).join(' ').replace(/\s+/g, ' ').trim().slice(0, 100);
    } catch { /* fall through */ }
  }
  return str.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 100);
}

const SENTIMENT_LABELS: Record<string, string> = {
  positive: 'Positive',
  neutral: 'Neutral',
  negative: 'Negative',
};

type InsightFilter = 'all' | 'created' | 'assigned' | 'unprocessed' | 'processed';
const INSIGHT_FILTERS: { value: InsightFilter; label: string }[] = [
  { value: 'all',         label: 'All insights' },
  { value: 'created',     label: "Insights I've created" },
  { value: 'assigned',    label: 'Insights assigned to me' },
  { value: 'unprocessed', label: 'Unprocessed insights' },
  { value: 'processed',   label: 'Processed insights' },
];

type TabKey = 'insights' | 'themes';

const InsightIcon: React.FC<{ color: string }> = ({ color }) => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2">
    <path d="M18 20V10M12 20V4M6 20v-6" />
  </svg>
);

const ThemeIcon: React.FC<{ color: string }> = ({ color }) => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2">
    <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/>
    <line x1="7" y1="7" x2="7.01" y2="7"/>
  </svg>
);

const Insights: React.FC = () => {
  const { activeProjectId } = useActiveProject();
  const { currentOrg, user } = useAuth();
  const { can } = usePermissions();
  const canManage = can('product_insights');

  const [activeTab, setActiveTab] = useState<TabKey>('insights');

  // Insights state
  const [insights, setInsights]       = useState<Insight[]>([]);
  const [loadingInsights, setLoadingInsights] = useState(true);
  const [insightsError, setInsightsError]     = useState<string | null>(null);

  // Themes state
  const [themes, setThemes]           = useState<Theme[]>([]);
  const [loadingThemes, setLoadingThemes] = useState(true);
  const [themesError, setThemesError]     = useState<string | null>(null);
  const [showAddThemeModal, setShowAddThemeModal] = useState(false);
  const [editingTheme, setEditingTheme] = useState<Theme | null>(null);
  const [boardPrefs, setBoardPrefs] = useState<BoardPrefs>(() => loadBoardPrefs(INSIGHTS_BOARD_KEY));
  const colVisible = (key: string) => isColumnVisible(boardPrefs.columns, key);

  // Shared UI state
  const [allMembers, setAllMembers]   = useState<OrgMember[]>([]);
  const [searchQuery, setSearchQuery] = useState('');

  // Insights UI state
  const [showInsightModal, setShowInsightModal] = useState(false);
  const [editingInsight, setEditingInsight]     = useState<Insight | null>(null);

  // Insights filters
  const [insightFilter, setInsightFilter] = useState<InsightFilter>('all');
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [dateFrom, setDateFrom] = useState<string>('');
  const [dateTo, setDateTo]     = useState<string>('');
  const [showDatePicker, setShowDatePicker] = useState(false);
  const filterRef = useRef<HTMLDivElement>(null);
  const dateRef   = useRef<HTMLDivElement>(null);

  // ── Loaders ─────────────────────────────────────────────
  const loadInsights = () => {
    setLoadingInsights(true);
    setInsightsError(null);
    insightService.getInsights(activeProjectId)
      .then(setInsights)
      .catch(() => setInsightsError('Failed to load insights'))
      .finally(() => setLoadingInsights(false));
  };

  const loadThemes = () => {
    setLoadingThemes(true);
    setThemesError(null);
    themeService.getThemes(activeProjectId)
      .then(setThemes)
      .catch(() => setThemesError('Failed to load themes'))
      .finally(() => setLoadingThemes(false));
  };

  useEffect(() => {
    if (activeTab === 'insights') loadInsights();
    if (activeTab === 'themes')   loadThemes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeProjectId, activeTab]);

  useEffect(() => {
    if (!currentOrg) return;
    organizationService.getMembersWithProfiles(currentOrg.id)
      .then(setAllMembers).catch(() => {});
  }, [currentOrg]);

  // Close filter dropdowns on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (filterRef.current && !filterRef.current.contains(e.target as Node)) {
        setShowFilterDropdown(false);
      }
      if (dateRef.current && !dateRef.current.contains(e.target as Node)) {
        setShowDatePicker(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // ── Insights filtering ──────────────────────────────────
  const filteredInsights = insights.filter(i => {
    if (searchQuery.trim() && !i.title.toLowerCase().includes(searchQuery.trim().toLowerCase())) {
      return false;
    }
    if (insightFilter === 'created' && user && i.created_by !== user.id) return false;
    if (insightFilter === 'assigned' && user && i.assignee_id !== user.id) return false;
    if (insightFilter === 'unprocessed' && i.processed) return false;
    if (insightFilter === 'processed' && !i.processed) return false;
    if (dateFrom) {
      const from = new Date(dateFrom);
      if (!isNaN(from.getTime()) && new Date(i.created_at) < from) return false;
    }
    if (dateTo) {
      const to = new Date(dateTo);
      to.setHours(23, 59, 59, 999);
      if (!isNaN(to.getTime()) && new Date(i.created_at) > to) return false;
    }
    return true;
  });

  const hasActiveFilters = insightFilter !== 'all' || !!dateFrom || !!dateTo;
  const clearAllFilters = () => {
    setInsightFilter('all');
    setDateFrom('');
    setDateTo('');
  };

  // ── Themes grouping (parent → children) ─────────────────
  const parentThemes = useMemo(() => themes.filter(t => !t.parent_id), [themes]);
  const childThemesByParent = useMemo(() => {
    const map = new Map<string, Theme[]>();
    themes.filter(t => t.parent_id).forEach(t => {
      const list = map.get(t.parent_id!) ?? [];
      list.push(t);
      map.set(t.parent_id!, list);
    });
    return map;
  }, [themes]);

  const filteredParentThemes = parentThemes.filter(t =>
    !searchQuery.trim() || t.name.toLowerCase().includes(searchQuery.trim().toLowerCase())
  );
  const filteredChildThemesByParent = useMemo(() => {
    const map = new Map<string, Theme[]>();
    filteredParentThemes.forEach(p => {
      const children = (childThemesByParent.get(p.id) ?? []).filter(c =>
        !searchQuery.trim() || c.name.toLowerCase().includes(searchQuery.trim().toLowerCase())
      );
      if (children.length) map.set(p.id, children);
    });
    return map;
  }, [filteredParentThemes, childThemesByParent, searchQuery]);

  // ── Handlers ────────────────────────────────────────────
  const handleInsightSaved = (insight: Insight) => {
    setInsights(prev => {
      const exists = prev.some(i => i.id === insight.id);
      return exists ? prev.map(i => i.id === insight.id ? insight : i) : [insight, ...prev];
    });
    setShowInsightModal(false);
    setEditingInsight(null);
  };

  const handleInsightDelete = async (insight: Insight, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!window.confirm(`Delete "${insight.title}"? This cannot be undone.`)) return;
    try {
      await insightService.deleteInsight(insight.id);
      setInsights(prev => prev.filter(i => i.id !== insight.id));
    } catch {
      alert('Failed to delete insight.');
    }
  };

  const handleThemeSaved = (theme: Theme) => {
    setThemes(prev => {
      const exists = prev.some(t => t.id === theme.id);
      return exists ? prev.map(t => t.id === theme.id ? theme : t) : [theme, ...prev];
    });
  };

  const handleThemeDelete = async (theme: Theme, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!window.confirm(`Delete "${theme.name}"? Its children will become top-level themes.`)) return;
    try {
      await themeService.deleteTheme(theme.id);
      setThemes(prev => prev.filter(t => t.id !== theme.id));
    } catch {
      alert('Failed to delete theme.');
    }
  };

  const getMemberName = (userId: string | null) => {
    if (!userId) return null;
    const m = allMembers.find(m => m.user_id === userId);
    return m ? (m.full_name || m.email || null) : null;
  };

  return (
    <Layout>
      <div className="customers-list-container">
        {/* ── Page header (tabs on left, search + add on right) ── */}
        <div className="customers-header-row">
          <div className="insights-tabs" role="tablist">
            <button
              role="tab"
              aria-selected={activeTab === 'insights'}
              className={`insights-tab ${activeTab === 'insights' ? 'active' : ''}`}
              onClick={() => setActiveTab('insights')}
            >
              <InsightIcon color={activeTab === 'insights' ? '#4338ca' : '#6b7280'} />
              <span>Insights</span>
              <span className="insights-tab-count">{insights.length}</span>
            </button>
            <button
              role="tab"
              aria-selected={activeTab === 'themes'}
              className={`insights-tab ${activeTab === 'themes' ? 'active' : ''}`}
              onClick={() => setActiveTab('themes')}
            >
              <ThemeIcon color={activeTab === 'themes' ? '#4338ca' : '#6b7280'} />
              <span>Themes</span>
              <span className="insights-tab-count">{themes.length}</span>
            </button>
          </div>

          <div className="header-right-section">
            <div className="search-bar">
              <svg className="search-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
              </svg>
              <input
                type="text"
                placeholder={activeTab === 'insights' ? 'Search insights...' : 'Search themes...'}
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                className="search-input"
              />
            </div>
            {activeTab === 'insights' && (
              <button className="customers-add-btn btn-primary-cta" onClick={() => { setEditingInsight(null); setShowInsightModal(true); }}
                disabled={!canManage} title={canManage ? 'Add a new insight' : 'Only owners and admins can add insights'}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                  <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
                </svg>
                Add Insight
              </button>
            )}
            {activeTab === 'themes' && (
              <button className="customers-add-btn btn-primary-cta" onClick={() => setShowAddThemeModal(true)}
                disabled={!canManage} title={canManage ? 'Add a new theme' : 'Only owners and admins can add themes'}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                  <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
                </svg>
                Add Theme
              </button>
            )}
          </div>
        </div>

        {/* ── Filter bar (only on Insights tab) ── */}
        {activeTab === 'insights' && (
          <div className="insights-filter-bar">
            <div className="insights-filter-group" ref={filterRef}>
              <button
                type="button"
                className={`insights-filter-btn ${insightFilter !== 'all' ? 'active' : ''}`}
                onClick={() => { setShowFilterDropdown(p => !p); setShowDatePicker(false); }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/>
                </svg>
                {INSIGHT_FILTERS.find(f => f.value === insightFilter)?.label}
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" style={{ marginLeft: 2 }}>
                  <polyline points="6 9 12 15 18 9"/>
                </svg>
              </button>
              {showFilterDropdown && (
                <div className="insights-filter-dropdown">
                  {INSIGHT_FILTERS.map(f => (
                    <button
                      key={f.value}
                      type="button"
                      className={`insights-filter-option ${insightFilter === f.value ? 'selected' : ''}`}
                      onClick={() => { setInsightFilter(f.value); setShowFilterDropdown(false); }}
                    >
                      {f.label}
                      {insightFilter === f.value && (
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="20 6 9 17 4 12"/>
                        </svg>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="insights-filter-group" ref={dateRef}>
              <button
                type="button"
                className={`insights-filter-btn ${(dateFrom || dateTo) ? 'active' : ''}`}
                onClick={() => { setShowDatePicker(p => !p); setShowFilterDropdown(false); }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                  <line x1="16" y1="2" x2="16" y2="6"/>
                  <line x1="8" y1="2" x2="8" y2="6"/>
                  <line x1="3" y1="10" x2="21" y2="10"/>
                </svg>
                {dateFrom || dateTo
                  ? `${dateFrom ? new Date(dateFrom).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '…'} – ${dateTo ? new Date(dateTo).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '…'}`
                  : 'Created date'}
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" style={{ marginLeft: 2 }}>
                  <polyline points="6 9 12 15 18 9"/>
                </svg>
              </button>
              {showDatePicker && (
                <div className="insights-date-picker">
                  <div className="insights-date-field">
                    <label>From</label>
                    <input
                      type="date"
                      value={dateFrom}
                      max={dateTo || undefined}
                      onChange={e => setDateFrom(e.target.value)}
                    />
                  </div>
                  <div className="insights-date-field">
                    <label>To</label>
                    <input
                      type="date"
                      value={dateTo}
                      min={dateFrom || undefined}
                      onChange={e => setDateTo(e.target.value)}
                    />
                  </div>
                  <div className="insights-date-actions">
                    <button type="button" className="insights-date-clear" onClick={() => { setDateFrom(''); setDateTo(''); }}>Clear</button>
                    <button type="button" className="insights-date-apply" onClick={() => setShowDatePicker(false)}>Apply</button>
                  </div>
                </div>
              )}
            </div>

            {hasActiveFilters && (
              <button type="button" className="insights-clear-filters" onClick={clearAllFilters}>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                  <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
                Clear filters
              </button>
            )}

            <BoardControlsButton
              columns={INSIGHT_COLUMNS}
              storageKey={INSIGHTS_BOARD_KEY}
              prefs={boardPrefs}
              onChange={setBoardPrefs}
            />
          </div>
        )}

        {/* ── Insights tab content ── */}
        {activeTab === 'insights' && (
          <>
            {loadingInsights && <div className="customers-loading">Loading insights…</div>}

            {insightsError && (
              <div className="error-state"><p>{insightsError}</p><button onClick={loadInsights}>Retry</button></div>
            )}

            {!loadingInsights && !insightsError && filteredInsights.length === 0 && (
              <div className="empty-state">
                <div className="empty-icon">💡</div>
                <h2>
                  {searchQuery || hasActiveFilters
                    ? 'No insights match your filters'
                    : 'No insights yet'}
                </h2>
                <p>
                  {searchQuery
                    ? 'Try a different search term'
                    : hasActiveFilters
                      ? 'Try adjusting or clearing your filters'
                      : 'Add your first insight to get started'}
                </p>
              </div>
            )}

            {!loadingInsights && !insightsError && filteredInsights.length > 0 && (
              <div className="clist-table-wrap">
                <div className="clist-header-row">
                  <div className="clist-col clist-col-name">Title</div>
                  {colVisible('description') && <div className="clist-col clist-col-desc">Description</div>}
                  {colVisible('assignee') && <div className="clist-col clist-col-sm">Assignee</div>}
                  {colVisible('customer') && <div className="clist-col clist-col-sm">Customer</div>}
                  {colVisible('sentiment') && <div className="clist-col clist-col-sm">Sentiment</div>}
                  {colVisible('themes') && <div className="clist-col clist-col-sm">Themes</div>}
                  <div className="clist-col clist-col-actions" />
                </div>

                {filteredInsights.map((insight, idx) => {
                  const colorIdx     = idx % 4;
                  const assigneeName = getMemberName(insight.assignee_id);
                  const customerName = insight.customer_users?.name ?? insight.companies?.name ?? null;
                  const preview      = getPlainText(insight.details);
                  return (
                    <div key={insight.id} className="clist-row"
                      onClick={() => { if (canManage) { setEditingInsight(insight); setShowInsightModal(true); } }}
                      style={{ cursor: canManage ? 'pointer' : 'default' }}>

                      <div className="clist-col clist-col-name">
                        <div className="clist-icon" style={{ background: ICON_BGS[colorIdx] }}>
                          <InsightIcon color={ICON_COLORS[colorIdx]} />
                        </div>
                        <span className="clist-name">{insight.title}</span>
                      </div>

                      {colVisible('description') && (
                        <div className="clist-col clist-col-desc">
                          <span className="clist-meta">{preview || <span className="clist-empty">—</span>}</span>
                        </div>
                      )}

                      {colVisible('assignee') && (
                        <div className="clist-col clist-col-sm">
                          {assigneeName
                            ? <span className="clist-pill clist-pill-purple">{assigneeName}</span>
                            : <span className="clist-empty">—</span>}
                        </div>
                      )}

                      {colVisible('customer') && (
                        <div className="clist-col clist-col-sm">
                          {customerName
                            ? <span className="clist-pill clist-pill-indigo">{customerName}</span>
                            : <span className="clist-empty">—</span>}
                        </div>
                      )}

                      {colVisible('sentiment') && (
                        <div className="clist-col clist-col-sm">
                          {insight.sentiment
                            ? <span className={`clist-pill clist-pill-${insight.sentiment === 'positive' ? 'green' : insight.sentiment === 'negative' ? 'red' : 'blue'}`}>{SENTIMENT_LABELS[insight.sentiment]}</span>
                            : <span className="clist-empty">—</span>}
                        </div>
                      )}

                      {colVisible('themes') && (
                        <div className="clist-col clist-col-sm">
                          {insight.themes?.length > 0
                            ? <span className="clist-pill clist-pill-blue">{insight.themes[0]}{insight.themes.length > 1 ? ` +${insight.themes.length - 1}` : ''}</span>
                            : <span className="clist-empty">—</span>}
                        </div>
                      )}

                      <div className="clist-col clist-col-actions" onClick={e => e.stopPropagation()}>
                        {canManage && (
                          <div className="clist-actions">
                            <button className="clist-action-btn"
                              onClick={e => { e.stopPropagation(); setEditingInsight(insight); setShowInsightModal(true); }} title="Edit">
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                                <path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z"/>
                              </svg>
                            </button>
                            <button className="clist-action-btn clist-action-danger"
                              onClick={e => handleInsightDelete(insight, e)} title="Delete">
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                                <polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                              </svg>
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}

        {/* ── Themes tab content ── */}
        {activeTab === 'themes' && (
          <>
            {loadingThemes && <div className="customers-loading">Loading themes…</div>}

            {themesError && (
              <div className="error-state"><p>{themesError}</p><button onClick={loadThemes}>Retry</button></div>
            )}

            {!loadingThemes && !themesError && parentThemes.length === 0 && (
              <div className="empty-state">
                <div className="empty-icon">🏷️</div>
                <h2>{searchQuery ? 'No themes match your search' : 'No themes yet'}</h2>
                <p>{searchQuery ? 'Try a different search term' : 'Add your first theme to start grouping related insights'}</p>
              </div>
            )}

            {!loadingThemes && !themesError && filteredParentThemes.length === 0 && parentThemes.length > 0 && (
              <div className="empty-state">
                <div className="empty-icon">🔍</div>
                <h2>No themes match your search</h2>
                <p>Try a different search term</p>
              </div>
            )}

            {!loadingThemes && !themesError && filteredParentThemes.length > 0 && (
              <div className="themes-list">
                {filteredParentThemes.map((parent, idx) => {
                  const children = filteredChildThemesByParent.get(parent.id) ?? [];
                  const colorIdx = idx % 4;
                  return (
                    <div key={parent.id} className="theme-card">
                      <div className="theme-card-header">
                        <div className="theme-card-icon" style={{ background: ICON_BGS[colorIdx] }}>
                          <ThemeIcon color={ICON_COLORS[colorIdx]} />
                        </div>
                        <div className="theme-card-info">
                          <div className="theme-card-name">{parent.name}</div>
                          {parent.description && <div className="theme-card-desc">{parent.description}</div>}
                        </div>
                        <div className="theme-card-meta">
                          {children.length > 0 && (
                            <span className="theme-card-children-count">
                              {children.length} {children.length === 1 ? 'child' : 'children'}
                            </span>
                          )}
                          {canManage && (
                            <div className="clist-actions" style={{ opacity: 1 }}>
                              <button className="clist-action-btn"
                                onClick={e => { e.stopPropagation(); setEditingTheme(parent); }} title="Edit">
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.12 2.12 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                                </svg>
                              </button>
                              <button className="clist-action-btn clist-action-danger"
                                onClick={e => handleThemeDelete(parent, e)} title="Delete">
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                                  <polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                                </svg>
                              </button>
                            </div>
                          )}
                        </div>
                      </div>

                      {children.length > 0 && (
                        <div className="theme-card-children">
                          {children.map(child => (
                            <div key={child.id} className={`theme-child-pill ${canManage ? 'theme-child-pill-editable' : ''}`}
                              onClick={() => { if (canManage) setEditingTheme(child); }}
                              title={canManage ? 'Edit theme' : undefined}>
                              <span className="theme-child-dot" style={{ background: ICON_COLORS[colorIdx] }} />
                              <span>{child.name}</span>
                              {canManage && (
                                <button className="theme-child-remove" onClick={e => handleThemeDelete(child, e)} title="Delete">
                                  <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round">
                                    <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                                  </svg>
                                </button>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>

      {showInsightModal && (
        <InsightModal insight={editingInsight}
          onClose={() => { setShowInsightModal(false); setEditingInsight(null); }}
          onSaved={handleInsightSaved} />
      )}

      {showAddThemeModal && (
        <AddThemeModal
          onClose={() => setShowAddThemeModal(false)}
          onSaved={handleThemeSaved}
          existingThemes={parentThemes}
        />
      )}

      {editingTheme && (
        <EditThemeModal
          theme={editingTheme}
          parentThemes={parentThemes}
          onClose={() => setEditingTheme(null)}
          onSaved={(t) => { handleThemeSaved(t); setEditingTheme(null); }}
        />
      )}
    </Layout>
  );
};

export default Insights;
